import React, { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useSearchParams } from "react-router-dom"
import {
  FaBan,
  FaCheckCircle,
  FaFileInvoiceDollar,
  FaMoneyCheckAlt,
  FaPaperPlane,
  FaPlus,
  FaRupeeSign,
  FaTimesCircle,
  FaTrash,
  FaTruckLoading,
} from "react-icons/fa"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateText,
  fetchPaged,
  FilterOption,
  fullName,
  materialFilters,
  money,
  num,
  qty,
  SupplierKind,
  supplierDisplayName,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  DetailRow,
  ExpandAction,
  Field,
  FilterField,
  FilterRow,
  GhostButton,
  IconAction,
  ListCard,
  OffCanvas,
  PrimaryButton,
  RefreshButton,
  ScopeNote,
  SearchBox,
  Select,
  StatusBadge,
  StatusTabs,
  SupplierPartyPicker,
  Td,
  TextArea,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Material Arrangement and Payment (MAP).
 *
 * Two things here are irreversible and they are deliberately separate steps:
 *   Pay      money leaves the company.
 *   Receive  material arrives on the books.
 *
 * They happen in either order - an advance purchase pays first, a credit
 * purchase receives first - and neither implies the other. Approving an
 * arrangement, or paying for it, puts nothing into stock.
 */

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pending_approval", label: "Pending Approval" },
  { key: "approved", label: "Approved" },
  { key: "proceed_for_payment", label: "For Payment" },
  { key: "paid", label: "Paid" },
  { key: "received", label: "Received" },
  { key: "rejected,cancelled", label: "Closed" },
]

interface LineDraft {
  key: string
  request_item_id: number | null
  item: FilterOption | null
  quantity: string
  unit_rate: string
}

const MaterialArrangementsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { confirm, prompt } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()

  const canCreate = hasPermission("MaterialArrangement", "create")
  const canApprove = hasPermission("MaterialArrangement", "approve")
  const canProceed = hasPermission("MaterialArrangement", "proceed_payment")
  const canPay = hasPermission("MaterialArrangement", "pay")
  const canReceive = hasPermission("MaterialArrangement", "receive")
  const canCancel = hasPermission("MaterialArrangement", "cancel")

  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState("")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 400)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  // The MRN list links here with ?request_id=, which opens the form against
  // that request rather than making the user find it again.
  const [formRequestId, setFormRequestId] = useState<number | null>(
    searchParams.get("request_id") ? Number(searchParams.get("request_id")) : null,
  )
  const [showForm, setShowForm] = useState(!!searchParams.get("request_id"))

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/arrangements", {
        page,
        limit,
        status: status || undefined,
        project_id: project?.id,
        search: debouncedSearch.trim() || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load arrangements"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, status, project, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  const closeForm = () => {
    setShowForm(false)
    setFormRequestId(null)
    if (searchParams.get("request_id")) {
      searchParams.delete("request_id")
      setSearchParams(searchParams, { replace: true })
    }
  }

  const act = async (
    row: any,
    action: "submit" | "approve" | "reject" | "proceed-for-payment" | "pay" | "receive" | "cancel",
  ) => {
    let body: Record<string, any> = {}

    if (action === "reject" || action === "cancel") {
      const entered = await prompt({
        title: action === "reject" ? "Reject arrangement" : "Cancel arrangement",
        message: "The arranger will see your reason.",
        label: "Reason",
        required: true,
        multiline: true,
        confirmText: action === "reject" ? "Reject" : "Cancel arrangement",
        cancelText: "Go back",
        variant: "warning",
      })
      if (entered === null) return
      body = { remarks: entered, reason: entered }
    }

    if (action === "proceed-for-payment") {
      const ok = await confirm({
        title: "Send for payment",
        message: `This releases ${money(row.total_amount)} into the cashier's payment queue.`,
        confirmText: "Send for payment",
        cancelText: "Not yet",
      })
      if (!ok) return
    }

    if (action === "pay") {
      const reference = await prompt({
        title: "Record payment",
        message: (
          <>
            Recording <strong>{money(row.total_amount)}</strong> paid to{" "}
            <strong>{supplierDisplayName(row, "the supplier")}</strong>. Money has left the
            company at this point, and the arrangement stops being editable.
          </>
        ),
        label: "Payment reference (UTR, cheque number)",
        placeholder: "Optional",
        confirmText: "Record payment",
        cancelText: "Go back",
        variant: "warning",
      })
      if (reference === null) return
      body = { payment_reference: reference || null }
    }

    if (action === "receive") {
      const signoff = await prompt({
        title: "Receive material",
        message:
          "This is the step that puts the material into stock. Everything outstanding on the arrangement is received in full; use the detail view for a partial receipt.",
        label: "Received by (name)",
        placeholder: "Who signed for the consignment",
        confirmText: "Receive into stock",
        cancelText: "Go back",
      })
      if (signoff === null) return
      body = { receiving_signoff_name: signoff }
    }

    setBusyId(row.id)
    try {
      const res = await api.post(`/materials/arrangements/${row.id}/${action}`, body)
      toast.success(res.data?.message || "Done")
      load()
    } catch (e: any) {
      toast.error(apiError(e, "The action could not be completed"))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaFileInvoiceDollar className="w-5 h-5" />}
        title="Material Arrangement & Payment (MAP)"
        subtitle="Supplier, rates, invoice and payment against an approved request. Paying and receiving are separate steps in either order - only receiving puts material into stock."
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canCreate ? (
              <PrimaryButton onClick={() => setShowForm(true)}>
                <FaPlus className="w-4 h-4" /> New Arrangement
              </PrimaryButton>
            ) : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Search" grow>
                <SearchBox
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setPage(1)
                  }}
                  placeholder="MAP number, supplier, invoice..."
                />
              </FilterField>
              <FilterField label="Project">
                <AsyncPicker
                  value={project}
                  onChange={(o) => {
                    setProject(o)
                    setPage(1)
                  }}
                  fetcher={(s) => materialFilters.projects(s)}
                  placeholder="All my projects"
                  emptyHint="You are not assigned to any project"
                />
              </FilterField>
            </FilterRow>
            <StatusTabs
              tabs={STATUS_TABS}
              value={status}
              onChange={(k) => {
                setStatus(k)
                setPage(1)
              }}
            />
          </>
        }
        footer={
          pagination ? (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          ) : null
        }
      >
        <DataTable
          colSpan={8}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading arrangements..."
          emptyText="No arrangements found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaFileInvoiceDollar />}
          head={
            <>
              <Th>Actions</Th>
              <Th>Arrangement</Th>
              <Th>Request</Th>
              <Th>Supplier</Th>
              <Th>Mode / Term</Th>
              <Th>Delivers To</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
            </>
          }
        >
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50 transition-colors">
                <Cell>
                  <div className="flex items-center gap-1">
                    <ExpandAction
                      open={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      what="items"
                    />
                    {row.status === "draft" && canCreate ? (
                      <IconAction title="Submit for approval" disabled={busyId === row.id} onClick={() => act(row, "submit")}>
                        <FaPaperPlane />
                      </IconAction>
                    ) : null}
                    {row.status === "pending_approval" && canApprove ? (
                      <>
                        <IconAction title="Approve" tone="green" disabled={busyId === row.id} onClick={() => act(row, "approve")}>
                          <FaCheckCircle />
                        </IconAction>
                        <IconAction title="Reject" tone="red" disabled={busyId === row.id} onClick={() => act(row, "reject")}>
                          <FaTimesCircle />
                        </IconAction>
                      </>
                    ) : null}
                    {row.status === "approved" && canProceed ? (
                      <IconAction
                        title="Proceed for payment"
                        tone="purple"
                        disabled={busyId === row.id}
                        onClick={() => act(row, "proceed-for-payment")}
                      >
                        <FaMoneyCheckAlt />
                      </IconAction>
                    ) : null}
                    {row.status === "proceed_for_payment" && canPay ? (
                      <IconAction title="Record payment" tone="emerald" disabled={busyId === row.id} onClick={() => act(row, "pay")}>
                        <FaRupeeSign />
                      </IconAction>
                    ) : null}
                    {["approved", "proceed_for_payment", "paid"].includes(row.status) && canReceive ? (
                      <IconAction title="Receive into stock" tone="indigo" disabled={busyId === row.id} onClick={() => act(row, "receive")}>
                        <FaTruckLoading />
                      </IconAction>
                    ) : null}
                    {!["received", "cancelled", "rejected"].includes(row.status) && canCancel ? (
                      <IconAction title="Cancel arrangement" tone="gray" disabled={busyId === row.id} onClick={() => act(row, "cancel")}>
                        <FaBan />
                      </IconAction>
                    ) : null}
                  </div>
                </Cell>
                <Cell>
                  <div className="font-medium">{row.arrangement_number}</div>
                  <div className="text-xs text-gray-500">
                    {row.project?.project_short_name || row.project?.project_name || "-"}
                  </div>
                </Cell>
                <Cell>{row.request?.request_number || "-"}</Cell>
                <Cell>{supplierDisplayName(row)}</Cell>
                <Cell>
                  <div className="capitalize">{row.purchase_mode}</div>
                  <div className="capitalize text-xs text-gray-500">{String(row.payment_term).replace("_", " ")}</div>
                </Cell>
                <Cell>
                  <div className="text-xs capitalize text-gray-500">{row.delivery_holder_type}</div>
                  <div>
                    {row.delivery_warehouse?.warehouse_name ||
                      fullName(row.delivery_user) ||
                      row.delivery_site?.site_name ||
                      "-"}
                  </div>
                </Cell>
                <Cell className="font-medium">{money(row.total_amount)}</Cell>
                <Cell>
                  <StatusBadge status={row.status} />
                  {row.paid_at ? <div className="text-[11px] text-emerald-700 mt-0.5">Paid {dateText(row.paid_at)}</div> : null}
                </Cell>
              </tr>
              {expanded === row.id ? (
                <DetailRow colSpan={8}>
                  <ArrangementDetail row={row} canReceive={canReceive} onDone={load} />
                </DetailRow>
              ) : null}
            </React.Fragment>
          ))}
        </DataTable>
      </ListCard>

      {showForm ? (
        <ArrangementForm
          initialRequestId={formRequestId}
          onClose={closeForm}
          onSaved={() => {
            closeForm()
            setPage(1)
            load()
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The detail row doubles as the partial-receipt screen: a supplier who sends
 * half the order is the normal case, and forcing an all-or-nothing receipt
 * would mean either booking material that has not arrived or leaving the
 * arrangement open with nothing on the books.
 */
const ArrangementDetail: React.FC<{ row: any; canReceive: boolean; onDone: () => void }> = ({
  row,
  canReceive,
  onDone,
}) => {
  const [receiving, setReceiving] = useState(false)
  const [quantities, setQuantities] = useState<Record<number, string>>({})
  const [signoff, setSignoff] = useState("")

  const receivable = ["approved", "proceed_for_payment", "paid"].includes(row.status)

  const submitPartial = async () => {
    const items = (row.items || [])
      .map((line: any) => ({ id: line.id, received_quantity: Number(quantities[line.id] ?? 0) }))
      .filter((line: any) => line.received_quantity > 0)
    if (!items.length) return toast.error("Enter a quantity against at least one line")

    setReceiving(true)
    try {
      const res = await api.post(`/materials/arrangements/${row.id}/receive`, {
        items,
        receiving_signoff_name: signoff || null,
      })
      toast.success(res.data?.message || "Material received")
      setQuantities({})
      onDone()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to receive material"))
    } finally {
      setReceiving(false)
    }
  }

  return (
    <div className="space-y-3 py-2">
      <table className="min-w-full text-sm bg-white border border-gray-200 rounded">
        <thead className="bg-gray-100">
          <tr>
            <Th>Item</Th>
            <Th>Ordered</Th>
            <Th>Received</Th>
            <Th>Outstanding</Th>
            <Th>Rate</Th>
            <Th>Amount</Th>
            {canReceive && receivable ? <Th>Receive now</Th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(row.items || []).map((line: any) => {
            const outstanding = num(line.quantity) - num(line.received_quantity)
            return (
              <tr key={line.id}>
                <Td>
                  {line.item?.item_name || `#${line.item_id}`}
                  {line.item?.item_code ? <span className="text-xs text-gray-400"> ({line.item.item_code})</span> : null}
                </Td>
                <Td>{qty(line.quantity)}</Td>
                <Td>{qty(line.received_quantity)}</Td>
                <Td className={outstanding > 0 ? "text-amber-700" : "text-green-700"}>{qty(outstanding)}</Td>
                <Td>{line.unit_rate ? money(line.unit_rate) : "-"}</Td>
                <Td>{line.amount ? money(line.amount) : "-"}</Td>
                {canReceive && receivable ? (
                  <Td>
                    <TextInput
                      type="number"
                      min="0"
                      max={outstanding}
                      step="0.001"
                      disabled={outstanding <= 0}
                      value={quantities[line.id] ?? ""}
                      placeholder={outstanding > 0 ? String(outstanding) : "-"}
                      onChange={(e) => setQuantities((q) => ({ ...q, [line.id]: e.target.value }))}
                      className="!w-28"
                    />
                  </Td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>

      {canReceive && receivable ? (
        <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded p-3">
          <div className="w-64">
            <Field label="Received by (signoff)">
              <TextInput value={signoff} onChange={(e) => setSignoff(e.target.value)} placeholder="Name" />
            </Field>
          </div>
          <PrimaryButton loading={receiving} onClick={submitPartial}>
            Receive entered quantities
          </PrimaryButton>
          <p className="text-[11px] text-gray-500 flex-1 min-w-[16rem]">
            Only the quantities typed above are received. This is the step that puts material into stock, against{" "}
            <strong className="capitalize">{row.delivery_holder_type}</strong>{" "}
            {row.delivery_warehouse?.warehouse_name || fullName(row.delivery_user) || row.delivery_site?.site_name || ""}.
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-600">
        <div>
          <span className="font-medium text-gray-800">Invoice:</span> {row.invoice_number || "-"} ({dateText(row.invoice_date)})
        </div>
        <div>
          <span className="font-medium text-gray-800">Basic / Tax / Freight:</span> {money(row.basic_amount)} /{" "}
          {money(row.tax_amount)} / {money(row.freight_amount)}
        </div>
        <div>
          <span className="font-medium text-gray-800">Bank:</span> {row.bank_name || "-"} {row.account_number || ""}{" "}
          {row.ifsc_code || ""}
        </div>
        <div>
          <span className="font-medium text-gray-800">UPI:</span> {row.upi_id || "-"}
        </div>
        <div>
          <span className="font-medium text-gray-800">Paid:</span>{" "}
          {row.paid_at ? `${money(row.paid_amount)} on ${dateText(row.paid_at)} (${row.payment_reference || "no ref"})` : "Not yet"}
        </div>
        <div>
          <span className="font-medium text-gray-800">Received:</span>{" "}
          {row.received_at ? `${dateText(row.received_at)} by ${row.receiving_signoff_name || fullName(row.receiver)}` : "Not yet"}
        </div>
        {row.rejection_remarks ? (
          <div className="md:col-span-2 text-red-700">
            <span className="font-medium">Rejected:</span> {row.rejection_remarks}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const ArrangementForm: React.FC<{
  initialRequestId: number | null
  onClose: () => void
  onSaved: () => void
}> = ({ initialRequestId, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [loadingRequest, setLoadingRequest] = useState(false)
  const [request, setRequest] = useState<any | null>(null)
  const [requestOption, setRequestOption] = useState<FilterOption | null>(null)
  const [supplier, setSupplier] = useState<FilterOption | null>(null)
  const [supplierKind, setSupplierKind] = useState<SupplierKind>("supplier")
  const [lines, setLines] = useState<LineDraft[]>([])
  const [form, setForm] = useState<Record<string, string>>({
    purchase_mode: "supplier",
    payment_term: "advance",
    invoice_number: "",
    invoice_date: "",
    expected_delivery_date: "",
    tax_amount: "",
    freight_amount: "",
    delivery_location: "",
    remarks: "",
  })
  const [files, setFiles] = useState<FileList | null>(null)

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }))

  // Approved requests are the only things an arrangement can hang off, so the
  // picker asks the request list for exactly those.
  const fetchRequests = useCallback(async (searchText: string): Promise<FilterOption[]> => {
    const res = await api.get("/materials/requests", {
      params: { status: "approved,arranged", search: searchText || undefined, limit: 25 },
    })
    return (res.data?.data?.data || []).map((r: any) => ({
      id: Number(r.id),
      code: r.project?.project_short_name || null,
      name: `${r.request_number} - ${r.inc_site?.site_name || r.site_name || r.project?.project_name || ""}`.trim(),
    }))
  }, [])

  const loadRequest = useCallback(async (id: number) => {
    setLoadingRequest(true)
    try {
      const res = await api.get(`/materials/requests/${id}`)
      const data = res.data?.data
      setRequest(data)
      // Carry every outstanding approved line over at its estimated rate; the
      // arranger overwrites the rate with what was actually negotiated.
      setLines(
        (data?.items || [])
          .map((line: any) => {
            const approved =
              line.approved_quantity === null || line.approved_quantity === undefined
                ? num(line.requested_quantity)
                : num(line.approved_quantity)
            const outstanding = approved - num(line.arranged_quantity)
            return {
              key: `r${line.id}`,
              request_item_id: Number(line.id),
              item: { id: Number(line.item_id), code: line.item?.item_code, name: line.item?.item_name || `#${line.item_id}` },
              quantity: outstanding > 0 ? String(outstanding) : "",
              unit_rate: line.estimated_rate ? String(line.estimated_rate) : "",
            } as LineDraft
          })
          .filter((l: LineDraft) => l.quantity !== ""),
      )
      if (data?.supplier) {
        setSupplierKind("supplier")
        setSupplier({ id: data.supplier.id, code: data.supplier.supplier_code, name: data.supplier.supplier_name })
      } else if (data?.supplier_vendor) {
        setSupplierKind("vendor")
        setSupplier({
          id: data.supplier_vendor.id,
          code: data.supplier_vendor.vendor_code || data.supplier_vendor.vendor_short_name,
          name: data.supplier_vendor.vendor_full_name,
        })
      }
      if (data?.delivery_location) set("delivery_location", data.delivery_location)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load the request"))
    } finally {
      setLoadingRequest(false)
    }
  }, [])

  useEffect(() => {
    if (initialRequestId) loadRequest(initialRequestId)
  }, [initialRequestId, loadRequest])

  const basic = lines.reduce((sum, l) => sum + Number(l.quantity || 0) * Number(l.unit_rate || 0), 0)
  const total = basic + Number(form.tax_amount || 0) + Number(form.freight_amount || 0)

  const submit = async (submitForApproval: boolean) => {
    const requestId = request?.id ?? requestOption?.id
    if (!requestId) return toast.error("Pick the approved request this arrangement fulfils")
    const validLines = lines.filter((l) => l.item && Number(l.quantity) > 0)
    if (!validLines.length) return toast.error("Add at least one line with a quantity")

    const payload = new FormData()
    payload.append("request_id", String(requestId))
    payload.append("submit", String(submitForApproval))
    if (supplier) payload.append(supplierKind === "vendor" ? "vendor_id" : "supplier_id", String(supplier.id))
    Object.entries(form).forEach(([key, value]) => {
      if (value) payload.append(key, value)
    })
    payload.append(
      "items",
      JSON.stringify(
        validLines.map((l) => ({
          request_item_id: l.request_item_id,
          item_id: l.item!.id,
          quantity: Number(l.quantity),
          unit_rate: l.unit_rate ? Number(l.unit_rate) : null,
        })),
      ),
    )
    if (files) Array.from(files).forEach((f) => payload.append("documents", f))

    setSaving(true)
    try {
      const res = await api.post("/materials/arrangements", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      toast.success(res.data?.message || "Arrangement created")
      onSaved()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to create the arrangement"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <OffCanvas
      onClose={onClose}
      title="New material arrangement"
      subtitle="Fix the supplier and the rates against an approved request. Nothing here spends money or moves stock - those are the pay and receive steps."
      width="xl"
      footer={
        <>
          <GhostButton onClick={() => submit(false)} disabled={saving}>
            Save as draft
          </GhostButton>
          <PrimaryButton loading={saving} onClick={() => submit(true)}>
            Submit for approval
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Approved request" required>
          {request ? (
            <div className="px-3 py-2 text-sm border border-gray-300 rounded bg-gray-50">
              {request.request_number}
              <span className="text-xs text-gray-500 block">
                {request.project?.project_name} - {request.inc_site?.site_name || request.site_name || "no site"}
              </span>
            </div>
          ) : (
            <AsyncPicker
              value={requestOption}
              onChange={(o) => {
                setRequestOption(o)
                if (o) loadRequest(o.id)
              }}
              fetcher={fetchRequests}
              placeholder="Select an approved request..."
              emptyHint="No approved requests waiting to be arranged"
            />
          )}
        </Field>
        <Field label="Supplier" hint="A vendor from the Vendor master can also be the supplier">
          <SupplierPartyPicker kind={supplierKind} onKindChange={setSupplierKind} value={supplier} onChange={setSupplier} />
        </Field>
        <Field label="Purchase mode" hint="Market means bought over the counter near the site">
          <Select value={form.purchase_mode} onChange={(e) => set("purchase_mode", e.target.value)}>
            <option value="supplier">From supplier</option>
            <option value="market">From market</option>
          </Select>
        </Field>

        <Field label="Payment term">
          <Select value={form.payment_term} onChange={(e) => set("payment_term", e.target.value)}>
            <option value="advance">Advance</option>
            <option value="on_delivery">On delivery</option>
            <option value="credit">Credit</option>
          </Select>
        </Field>
        <Field label="Invoice / PI number">
          <TextInput value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} />
        </Field>
        <Field label="Invoice date">
          <TextInput type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} />
        </Field>

        <Field label="Expected delivery">
          <TextInput
            type="date"
            value={form.expected_delivery_date}
            onChange={(e) => set("expected_delivery_date", e.target.value)}
          />
        </Field>
        <Field label="Tax amount">
          <TextInput
            type="number"
            min="0"
            step="0.01"
            value={form.tax_amount}
            onChange={(e) => set("tax_amount", e.target.value)}
          />
        </Field>
        <Field label="Freight amount">
          <TextInput
            type="number"
            min="0"
            step="0.01"
            value={form.freight_amount}
            onChange={(e) => set("freight_amount", e.target.value)}
          />
        </Field>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Lines</h3>
        {loadingRequest ? (
          <p className="text-sm text-gray-500">Loading the request lines...</p>
        ) : lines.length === 0 ? (
          <p className="text-sm text-gray-500">
            Pick a request above. Its outstanding approved lines are carried over automatically.
          </p>
        ) : (
          <div className="border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th className="w-2/5">Item</Th>
                  <Th>Quantity</Th>
                  <Th>Negotiated rate</Th>
                  <Th>Amount</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((line, index) => (
                  <tr key={line.key}>
                    <Td>{line.item?.name}</Td>
                    <Td>
                      <TextInput
                        type="number"
                        min="0"
                        step="0.001"
                        value={line.quantity}
                        onChange={(e) =>
                          setLines((ls) => ls.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))
                        }
                      />
                    </Td>
                    <Td>
                      <TextInput
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.unit_rate}
                        onChange={(e) =>
                          setLines((ls) => ls.map((l, i) => (i === index ? { ...l, unit_rate: e.target.value } : l)))
                        }
                      />
                    </Td>
                    <Td>{money(Number(line.quantity || 0) * Number(line.unit_rate || 0))}</Td>
                    <Td>
                      <button
                        onClick={() => setLines((ls) => ls.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <Td className="font-medium">Basic</Td>
                  <Td colSpan={2}></Td>
                  <Td className="font-medium">{money(basic)}</Td>
                  <Td></Td>
                </tr>
                <tr>
                  <Td className="font-semibold">Total payable</Td>
                  <Td colSpan={2} className="text-xs text-gray-500">
                    basic + tax + freight
                  </Td>
                  <Td className="font-semibold">{money(total)}</Td>
                  <Td></Td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Delivery location">
          <TextInput value={form.delivery_location} onChange={(e) => set("delivery_location", e.target.value)} />
        </Field>
        <Field label="Remarks">
          <TextArea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
        </Field>
      </div>

      <Field label="Documents" hint="Proforma invoice, tax invoice, payment QR">
        <input
          type="file"
          multiple
          onChange={(e) => setFiles(e.target.files)}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:bg-gray-100 file:text-gray-700"
        />
      </Field>
    </OffCanvas>
  )
}

export default MaterialArrangementsPage
